import type { Link } from '@unhead/vue'
import { withTrailingSlash } from 'ufo'
import type { I18nHeadMetaInfo, MetaAttrs } from '@nuxtjs/i18n'
import type { RouteLocationNormalizedLoaded } from 'vue-router'
import { isIndexedRoutePath } from '~/utils/robotsHelpers'

export interface SeoHelperConfig {
  allowedParams?: string[]
  shouldBeIndexed?: boolean
  canonicalLink?: string
  disableCanonicalLink?: boolean
  meta?: MetaAttrs
  link?: MetaAttrs[]
  /**
   * Explicitly signals that this page manages its own hreflang links via the
   * `link` option. When true, all auto-generated alternate links from
   * useLocaleHead are removed even if `link` is temporarily empty (e.g. during
   * client hydration before async data is restored).
   */
  hasCustomHreflang?: boolean
}

type MetaItem = MetaAttrs

type LinkItem = Link

interface SeoHelperVariables {
  $i18n: { locale: string }
  $route: RouteLocationNormalizedLoaded
  headers: Readonly<Record<string, string>>
  isSubdomain: boolean
  isProduction: boolean
  $nuxtI18nHead: I18nHeadMetaInfo
}

/**
 * @description //
 */

class SeoHelper {
  private vm: SeoHelperVariables
  private readonly allowedParams: string[]
  private readonly shouldBeIndexed: boolean
  private readonly canonicalLink: string | undefined
  private readonly disableCanonicalLink: boolean
  private readonly hasCustomHreflang: boolean | undefined
  private readonly i18nHead: I18nHeadMetaInfo
  private readonly i18nHeadMeta: MetaAttrs[]
  private meta: any
  private link: MetaAttrs[]

  constructor(vm: SeoHelperVariables, config: SeoHelperConfig) {
    this.vm = vm
    this.allowedParams = config.allowedParams || []
    this.shouldBeIndexed = config.shouldBeIndexed || false
    this.canonicalLink = config.canonicalLink || undefined
    this.disableCanonicalLink = config.disableCanonicalLink || false
    this.hasCustomHreflang = config.hasCustomHreflang
    this.meta = config.meta || true
    this.link = config.link || []

    this.i18nHead = this.vm.$nuxtI18nHead

    this.i18nHead.link = (this.i18nHead.link || []).map((link) =>
      this.normalizeLinkTag(link)
    )
    this.i18nHeadMeta = [...(this.i18nHead.meta || [])]

    this.init()
  }

  /**
   * Converts legacy 'hid' attribute to modern 'key' for consistent deduplication.
   */
  private normalizeLinkTag(link: MetaAttrs): MetaAttrs {
    if (link.hid && !link.key) {
      const { hid, ...rest } = link
      return { key: hid, ...rest }
    }
    return link
  }

  public getCanonicalLinkTag(): LinkItem | null {
    return this.i18nHead.link!.find((item) => item.key === 'i18n-can') || null
  }

  public getHreflangTags(): LinkItem[] {
    return (
      this.i18nHead.link!.filter((item) => item.hreflang !== undefined) || null
    )
  }

  public getCanonicalLink(): string | Promise<string | undefined> | undefined {
    return this.getCanonicalLinkTag()?.href || undefined
  }

  private isPropertyBasedMeta(key: string): boolean {
    return (
      key.startsWith('og:') ||
      key.startsWith('fb:') ||
      key.startsWith('article:') ||
      key.startsWith('product:')
    )
  }

  /**
   * @description
   */
  public createOrOverwriteMetaTag(
    metaKey: string,
    metaNewContent: string,
    metaNewName: string | null = null
  ): void {
    const metaTag = this.i18nHeadMeta.find((item) => item.key === metaKey)

    if (metaTag) {
      metaTag.content = metaNewContent
    } else {
      const isPropertyBased = this.isPropertyBasedMeta(metaKey)
      const tagName = metaNewName || metaKey

      this.i18nHeadMeta.push({
        key: metaKey,
        ...(isPropertyBased ? { property: tagName } : { name: tagName }),
        content: metaNewContent,
      })
    }
  }

  /**
   * @description
   */
  public createOrOverwriteLinkTag(key: string, linkObj: MetaAttrs): void {
    const tag = this.i18nHead.link!.find((item) => item.key === key)

    if (tag) {
      Object.assign(tag, linkObj)
    } else {
      this.i18nHead.link!.push({
        key,
        ...linkObj,
      })
    }
  }

  /**
   * @description Checks if the params are allowed
   * If there is a not allowed parameter, marks as noindex
   */
  public isQueryIsCanonical(): boolean {
    return Object.keys(this.getQueryParameters()).every((param) =>
      this.allowedParams.includes(param)
    )
  }

  public getHeadMeta(): MetaItem[] {
    return this.i18nHeadMeta
  }

  public getI18nHead(): I18nHeadMetaInfo {
    return this.i18nHead
  }

  /**
   * @description initialize helper
   */
  private init() {
    this.handlePageIndex()

    this.handleMetaTags()
    this.handleLinkTags()

    this.handleCanonicalLink()
    this.forceTrailingSlashOnHrefLangTags()
  }

  private getShouldBeIndexed() {
    return this.shouldBeIndexed
  }

  private setCanonicalLink() {
    if (this.canonicalLink) {
      const canonicalLinkTag = this.getCanonicalLinkTag()
      if (canonicalLinkTag) {
        canonicalLinkTag.href = this.canonicalLink
      }
    }
  }

  private handleCanonicalLink() {
    this.setCanonicalLink()

    if (this.disableCanonicalLink) {
      const canonicalLinkTag = this.getCanonicalLinkTag()
      if (canonicalLinkTag) {
        delete canonicalLinkTag.href
        delete canonicalLinkTag.rel
      }
    } else {
      this.forceTrailingSlashOnCanonical()
    }
  }

  private handlePageIndex() {
    const defaultRobotsContentArr = ['max-image-preview:large']
    const getRobotsMetaContent = (content: string) =>
      [content, defaultRobotsContentArr].join(', ')

    // Doesn't index as default
    this.createOrOverwriteMetaTag(
      'robots',
      getRobotsMetaContent('noindex, follow')
    )

    // Indexed in case:
    // - the route is listed in indexedRoutes array
    // - SeoHelper config is marked as shouldIndex
    if (
      isIndexedRoutePath(this.vm.$route.path, this.vm.$i18n.locale) ||
      this.getShouldBeIndexed()
    ) {
      this.createOrOverwriteMetaTag(
        'robots',
        getRobotsMetaContent('index, follow')
      )
    }
    // If the query is not canonical, then it can't be indexed by any means
    // If x-param header is 'param', don't index
    if (
      this.vm.headers['x-param'] === 'param' ||
      !this.isQueryIsCanonical() ||
      !this.vm.isProduction ||
      this.vm.isSubdomain
    ) {
      this.createOrOverwriteMetaTag(
        'robots',
        getRobotsMetaContent('noindex, follow')
      )
    }
  }

  private handleMetaTags() {
    Object.keys(this.meta).forEach((metaTagKey) => {
      const metaTagValue = this.meta[metaTagKey]
      if (metaTagValue && metaTagValue !== '') {
        this.createOrOverwriteMetaTag(metaTagKey, metaTagValue)
      }
    })
  }

  private handleLinkTags() {
    // Explicit flag takes priority; otherwise fall back to detecting hreflang
    // entries in the link array. Using the explicit flag is required when the
    // link array may be empty on first render (e.g. async data not yet restored
    // during client hydration) but the page still intends to own its hreflang.
    const hasCustomHreflang =
      this.hasCustomHreflang ??
      this.link.some((linkTag) => linkTag.hreflang !== undefined)

    // When the caller supplies custom hreflang links, remove all auto-generated
    // ones so only the explicitly provided locales are advertised. This prevents
    // hreflang from pointing to pages that don't have a localised version.
    if (hasCustomHreflang) {
      this.i18nHead.link = this.i18nHead.link!.filter(
        (tag) => tag.rel !== 'alternate'
      )
    }

    this.link.forEach((linkTag) => {
      this.createOrOverwriteLinkTag(linkTag?.key || '', linkTag)
    })
  }

  private forceTrailingSlashOnCanonical() {
    const canLinkTag = this.getCanonicalLinkTag()

    if (canLinkTag && canLinkTag.href && typeof canLinkTag.href === 'string') {
      canLinkTag.href = withTrailingSlash(canLinkTag.href, true)
    }
  }

  private forceTrailingSlashOnHrefLangTags() {
    const linkTags = this.getHreflangTags()
    linkTags.forEach((tag) => {
      // We also remove the default param (added by cloudfront) from hreflang
      const href = tag.href

      if (href && typeof href === 'string') {
        const indexOfParams = href.indexOf('?')

        // Might brake if we have indexable urls with params
        tag.href = withTrailingSlash(
          href.slice(0, indexOfParams > 0 ? indexOfParams : href.length),
          true
        )
      }
    })
  }

  private getQueryParameters(): any {
    return this.vm.$route.query
  }
}

export default SeoHelper
